import { GridRenderCellParams } from "@mui/x-data-grid";
import { formatNumber, fPercent } from "src/utils/format-number";
import { ColumnFeatures } from "../../features";
import { palette } from "../../../figma-palette";
import { LightTooltip } from "../../tooltips/light";
import { Chip, Stack, Typography } from "@mui/material";
import useLocales from "src/locales/use-locales";
import { formatCurrency } from "src/utils/format-currency";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { WorkspaceList } from "src/context/api/workspaces";

export { default as search_term } from "./search-term";

const getDiffValue = (field: string) => (params: GridRenderCellParams): number => Number(params?.row?.diff?.[field] ?? 0);
const isPositiveDiffGood = (field: string) => (params: GridRenderCellParams): boolean => getDiffValue(field)(params) > 0;
const isNegativeDiffGood = (field: string) => (params: GridRenderCellParams): boolean => getDiffValue(field)(params) < 0;

export const monthly_search_volumes = {
    field: 'monthly_search_volumes',
    position: 3,
    headerName: 'dashboard.nb_brand_grid.columns.title.monthly_search_volumes',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.monthly_search_volumes',
    cellClassName: 'column--data-column',
    disableColumnMenu: true,
    format: formatNumber,
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    diff: {
        good: isPositiveDiffGood,
    },
    renderCell: (params: GridRenderCellParams) => {
        return formatNumber(params.row.monthly_search_volumes);
    },
}

export const visibility_rate = {
    field: 'visibility_rate',
    position: 4,
    headerName: 'dashboard.nb_brand_grid.columns.title.visibility_rate',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.visibility_rate',

    
    color: palette.visibility,
    format: fPercent,
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF, ColumnFeatures.SIMPLEBAR],
    diff: {
        good: isPositiveDiffGood,
    },
    renderCell: (params: GridRenderCellParams) => {
        const color = palette.visibility_range.find(
            ({ min, max }) => {
                return params.row.visibility_rate >= min && params.row.visibility_rate <= max
            }
        )?.color;

        return (
            <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                py={1}
                sx={{ width: '100%', height: '52px' }}
            >
                <Typography variant="body2">
                    {fPercent(params.row.visibility_rate)}
                </Typography>
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    py={1}
                    sx={{ width: '100%', height: '52px' }}
                >
                    <Stack
                        sx={{
                            width: `${params.row.visibility_rate}%`,
                            height: '8px',
                            backgroundColor: color,
                            borderRadius: '6px',
                        }}
                    />
                </Stack>
            </Stack>
        );
    },
}

export const top5_compet_total = {
    field: 'top5_compet_total',
    position: 5,
    headerName: 'dashboard.nb_brand_grid.columns.title.top5_compet_total',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.top5_compet_total',
    transform: (data: any) => (data.map((item: any) => Object.assign({}, item, { rate_field: item.total_rate }))),
    
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.RANK],
}

export const top5_search_rate = {
    field: 'top5_search_rate',
    position: 6,
    headerName: 'dashboard.nb_brand_grid.columns.title.top5_search_rate',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.top5_search_rate',

    
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.RANK],
    transform: (data: any) => (data.map((item: any) => Object.assign({}, item, { rate_field: item.search_rate }))),
}

export const top5_shopping_rate = {
    field: 'top5_shopping_rate',
    position: 7,
    headerName: 'dashboard.nb_brand_grid.columns.title.top5_shopping_rate',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.top5_shopping_rate',
    transform: (data: any) => (data.map((item: any) => Object.assign({}, item, { rate_field: item.shopping_rate }))),
    
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.RANK],
}

export const top5_organic_rate = {
    field: 'top5_organic_rate',
    position: 8,
    headerName: 'dashboard.nb_brand_grid.columns.title.top5_organic_rate',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.top5_organic_rate',
    transform: (data: any) => (data.map((item: any) => Object.assign({}, item, { rate_field: item.organic_rate }))),
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.RANK],
}

export const avg_seo_position = {
    field: 'avg_seo_position',
    position: 9,
    headerName: 'dashboard.nb_brand_grid.columns.title.avg_seo_position',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.avg_seo_position',
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    diff: {
        good: isNegativeDiffGood,
    },
}

export const ad_cost = {
    field: 'ad_cost',
    position: 22,
    headerName: 'dashboard.nb_brand_grid.columns.title.ad_cost',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.ad_cost',

    
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    diff: {
        good: isNegativeDiffGood,
    },
    renderCell: (params: GridRenderCellParams) => {
        const { currency } = useCurrentWorkspace() as WorkspaceList;
        const { currentLang } = useLocales();
        const locale = currentLang.value || 'en-US';
        return formatCurrency(params.row.ad_cost, currency, locale, true, 2);
    }
}

export const ad_cpc = {
    field: 'ad_cpc',
    position: 26,
    headerName: 'dashboard.nb_brand_grid.columns.title.ad_cpc',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.ad_cpc',

    
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    diff: {
        good: isNegativeDiffGood,
    },
    renderCell: (params: GridRenderCellParams) => {
        const { currency } = useCurrentWorkspace() as WorkspaceList;
        const { currentLang } = useLocales();
        const locale = currentLang.value || 'en-US';
        return formatCurrency(params.row.ad_cpc, currency, locale, true, 2);
    }
}

export const paid_roas = {
    field: 'paid_roas',
    position: 23,
    headerName: 'dashboard.nb_brand_grid.columns.title.paid_roas',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.paid_roas',

    
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    diff: {
        good: isPositiveDiffGood,
    },
}

export const paid_cpa = {
    field: 'paid_cpa',
    position: 24,
    headerName: 'dashboard.nb_brand_grid.columns.title.paid_cpa',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.paid_cpa',

    
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    diff: {
        good: isNegativeDiffGood,
    },
    renderCell: (params: GridRenderCellParams) => {
        const { currency } = useCurrentWorkspace() as WorkspaceList;
        const { currentLang } = useLocales();
        const locale = currentLang.value || 'en-US';
        return formatCurrency(params.row.paid_cpa, currency, locale, true, 2);
    }
}
export const paid_search_cvr = {
    field: 'paid_search_cvr',
    position: 27,
    headerName: 'dashboard.nb_brand_grid.columns.title.paid_search_cvr',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.paid_search_cvr',
    width: 50,
    type: 'number',
    diff: {
        good: isPositiveDiffGood,
    },
    renderCell: (params: GridRenderCellParams) => {
        return fPercent(params.row.paid_search_cvr);
    },
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
}
export const paid_shopping_cvr = {
    field: 'paid_shopping_cvr',
    position: 28,
    headerName: 'dashboard.nb_brand_grid.columns.title.paid_shopping_cvr',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.paid_shopping_cvr',

    
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    diff: {
        good: isPositiveDiffGood,
    },
    renderCell: (params: GridRenderCellParams) => {
        return fPercent(params.row.paid_shopping_cvr);
    },
}
export const organic_cvr = {
    field: 'organic_cvr',
    position: 29,
    headerName: 'dashboard.nb_brand_grid.columns.title.organic_cvr',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.organic_cvr',
    
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    diff: {
        good: isPositiveDiffGood,
    },
    renderCell: (params: GridRenderCellParams) => {
        return fPercent(params.row.organic_cvr);
    },
}
export const cvr = {
    field: 'cvr',
    position: 21,
    headerName: 'dashboard.nb_brand_grid.columns.title.cvr',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.cvr',

    
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    diff: {
        good: isPositiveDiffGood,
    },
    renderCell: (params: GridRenderCellParams) => {
        return fPercent(params.row.cvr);
    },
}
export const ctr = {
    field: 'ctr',
    position: 15,
    headerName: 'dashboard.nb_brand_grid.columns.title.ctr',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.ctr',
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    width: 50,
    diff: {
        good: isPositiveDiffGood,
    },
    renderCell: (params: GridRenderCellParams) => {
        return fPercent(params.row.ctr);
    },
}
export const organic_ctr = {
    field: 'organic_ctr',
    position: 18,
    headerName: 'dashboard.nb_brand_grid.columns.title.organic_ctr',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.organic_ctr',
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    diff: {
        good: isPositiveDiffGood,
    },
    renderCell: (params: GridRenderCellParams) => {
        return fPercent(params.row.organic_ctr);
    },
}
export const paid_search_ctr = {
    field: 'paid_search_ctr',
    position: 16,
    headerName: 'dashboard.nb_brand_grid.columns.title.paid_search_ctr',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.paid_search_ctr',
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    diff: {
        good: isPositiveDiffGood,
    },
    renderCell: (params: GridRenderCellParams) => {
        return fPercent(params.row.paid_search_ctr);
    },
}
export const paid_shopping_ctr = {
    field: 'paid_shopping_ctr',
    position: 17,
    headerName: 'dashboard.nb_brand_grid.columns.title.paid_shopping_ctr',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.paid_shopping_ctr',

    
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    diff: {
        good: isPositiveDiffGood,
    },
    renderCell: (params: GridRenderCellParams) => {
        return fPercent(params.row.paid_shopping_ctr);
    },
}
export const clicks_split = {
    field: 'clicks_split',
    position: 14,
    headerName: 'dashboard.nb_brand_grid.columns.title.clicks_split',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.clicks_split',
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.SPLIT],
    format: formatNumber,
    transform: (params: GridRenderCellParams) => ({
        total: params.row.clicks_split.total_clicks,
        data: [
            {
                name: 'dashboard.nb_brand_grid.columns.title.organic',
                value: params.row.clicks_split.percent_organic_clicks,
                color: palette.organic,
                format: fPercent,
            },
            {
                name: 'dashboard.nb_brand_grid.columns.title.paid_search',
                value: params.row.clicks_split.percent_ad_clicks_from_search,
                color: palette.paid.search,
                format: fPercent,
            },
            {
                name: 'dashboard.nb_brand_grid.columns.title.paid_shopping',
                value: params.row.clicks_split.percent_ad_clicks_from_shopping,
                color: palette.paid.shopping,
                format: fPercent,
            },
        ],
    }),
}
export const paid_search_share = {
    field: 'paid_search_share',
    position: 10,
    headerName: 'dashboard.nb_brand_grid.columns.title.paid_search_share',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.paid_search_share',

    
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.SPLIT],
    format: fPercent,
    transform: (params: GridRenderCellParams) => ({
        total: params.row.paid_search_share.paid_search_visibility_rate,
        data: [
            {
                name: 'dashboard.nb_brand_grid.columns.competitors.1st_position',
                value: params.row.paid_search_share.first_search_visibility_rate,
                color: palette.paidSearchShare.first,
            },
            {
                name: 'dashboard.nb_brand_grid.columns.competitors.2-3_position',
                value: params.row.paid_search_share.top_search_visibility_rate,
                color: palette.paidSearchShare.then,
            },
            {
                name: 'dashboard.nb_brand_grid.columns.competitors.competitors',
                value: params.row.paid_search_share.compet_search_visibility_rate,
                color: palette.paidSearchShare.compet,
            },
        ],
    }),
}
export const paid_shopping_share = {
    field: 'paid_shopping_share',
    position: 11,
    headerName: 'dashboard.nb_brand_grid.columns.title.paid_shopping_share',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.paid_shopping_share',

    
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.SPLIT],
    format: fPercent,
    transform: (params: GridRenderCellParams) => ({
        total: params.row.paid_shopping_share.paid_shopping_visibility_rate,
        data: [
            {
                name: 'dashboard.nb_brand_grid.columns.competitors.1-3_position',
                value: params.row.paid_shopping_share.last_shopping_visibility_rate,
                color: palette.paidShoppingShare.first,
            },
            {
                name: 'dashboard.nb_brand_grid.columns.competitors.4-10_position',
                value: params.row.paid_shopping_share.top_shopping_visibility_rate,
                color: palette.paidShoppingShare.then,
            },
            {
                name: 'dashboard.nb_brand_grid.columns.competitors.competitors',
                value: params.row.paid_shopping_share.compet_shopping_visibility_rate,
                color: palette.paidShoppingShare.compet,
            },
        ],
    }),
}
export const impressions = {
    field: 'impressions',
    position: 12,
    headerName: 'dashboard.nb_brand_grid.columns.title.impressions',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.impressions',

    
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.SPLIT],
    format: formatNumber,
    transform: (params: GridRenderCellParams) => ({
        total: params.row.impressions.impressions,
        data: [
            {
                name: 'dashboard.nb_brand_grid.columns.title.organic',
                value: params.row.impressions.organic_impressions,
                color: palette.organic,
            },
            {
                name: 'dashboard.nb_brand_grid.columns.title.paid_search',
                value: params.row.impressions.paid_search_impressions,
                color: palette.paid.search,
            },
            {
                name: 'dashboard.nb_brand_grid.columns.title.paid_shopping',
                value: params.row.impressions.paid_shopping_impressions,
                color: palette.paid.shopping,
            },
        ],
    }),
}
export const conversions = {
    field: 'conversions',
    position: 19,
    headerName: 'dashboard.nb_brand_grid.columns.title.conversions',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.conversions',

    
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.SPLIT],
    diff: {
        good: isPositiveDiffGood,
    },
    format: formatNumber,
    transform: (params: GridRenderCellParams) => ({
        total: params.row.conversions.total_conversions,
        data: [
            {
                name: 'dashboard.nb_brand_grid.columns.title.organic',
                value: params.row.conversions.organic_conversions,
                color: palette.organic,
            },
            {
                name: 'dashboard.nb_brand_grid.columns.title.paid_search',
                value: params.row.conversions.paid_search_conversions,
                color: palette.paid.search,
            },
            {
                name: 'dashboard.nb_brand_grid.columns.title.paid_shopping',
                value: params.row.conversions.paid_shopping_conversions,
                color: palette.paid.shopping,
            },
        ],
    }),
}
export const revenue = {
    field: 'revenue',
    position: 20,
    headerName: 'dashboard.nb_brand_grid.columns.title.revenue',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.revenue',

    
    type: 'number',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.SPLIT],
    diff: {
        good: isPositiveDiffGood,
    },
    format: formatNumber,
    transform: (params: GridRenderCellParams) => ({
        total: params.row.revenue.revenue,
        data: [
            {
                name: 'dashboard.nb_brand_grid.columns.title.organic',
                value: params.row.revenue.organic_revenue,
                color: palette.organic,
            },
            {
                name: 'dashboard.nb_brand_grid.columns.title.paid_search',
                value: params.row.revenue.paid_search_revenue,
                color: palette.paid.search,
            },
            {
                name: 'dashboard.nb_brand_grid.columns.title.paid_shopping',
                value: params.row.revenue.paid_shopping_revenue,
                color: palette.paid.shopping,
            },
        ],
    }),
}
export const opportunity = {
    field: 'opportunity',
    position: 25,
    headerName: 'dashboard.nb_brand_grid.columns.title.opportunity',
    headerTooltip: 'dashboard.nb_brand_grid.columns.tooltip.opportunity',

    
    type: 'string',
    features: [ColumnFeatures.COMPARISON, ColumnFeatures.DIFF],
    renderCell: (params: GridRenderCellParams) => {
        const { t } = useLocales();
        const opportunityKey = params.row.opportunity.toLowerCase().replace(/ /g, '');
        const opportunityMap: Record<string, string> = {
            no_opportunity: t('dashboard.nb_brand_grid.opportunities.noOpportunity'),
            overlap: t('dashboard.nb_brand_grid.opportunities.overlap'),
            seo_boost: t('dashboard.nb_brand_grid.opportunities.organicBoost'),
            sea_boost: t('dashboard.nb_brand_grid.opportunities.cpcBoost'),
        };
        const label =
            opportunityMap[opportunityKey.toLowerCase()] ||
            params.row.opportunity.charAt(0).toUpperCase() + params.row.opportunity.slice(1);
        return (
            <LightTooltip
                title={
                    opportunityKey !== 'no_opportunity'
                        ? t('dashboard.nb_brand_grid.tooltips.' + opportunityKey)
                        : ''
                }
            >
                <Chip
                    label={label}
                    className={`opportunity-chip opportunity-chip-${params.row.opportunity}`}
                    size="small"
                />
            </LightTooltip>
        );
    }
}